import { Check, ChevronRight } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BottomSheet, BottomSheetTextButton } from '@/components/mobile';
import { useLocationDepartments } from '@/components/event-calendar/utils/departments';
import {
	CAPTION_TONE_CLASS,
	CheckList,
	CheckRow,
	Group,
	SwitchRow,
} from '@/components/settings/ui/grouped/Grouped';
import { SCREEN_SIZES, useMediaQuery } from '@/components/ui/hooks/use-media-query';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUserDisplayNames } from '@/hooks/useUserColorOptions';
import { cn } from '@/lib/utils';
import { RootState } from '@/reducers';
import {
	assignedCaption,
	creatorCaption,
	notificationPeople,
	notificationSentence,
	notificationSummary,
	toggleOffset,
	whenOptions,
	type NotificationKind,
	type Notifications,
} from './notificationSettings';

interface NotificationSettingsFieldProps {
	/** For the row's <Label htmlFor> */
	id?: string;
	kind: NotificationKind;
	/** The draft lives in the form, so rotating across 768px keeps it */
	value: Notifications;
	onChange: (value: Notifications) => void;
	allDay?: boolean;
	assigneeIds: (string | undefined)[];
	departmentId?: string | null;
	creatorId?: string | null;
	/** Nothing is saved yet, so whoever is filling this in is the creator */
	isNew?: boolean;
	disabled?: boolean;
	/** The panel was open when a layout swap remounted the form: open it again */
	reopen?: boolean;
	onOpenChange?: (open: boolean) => void;
	/**
	 * 'row' (default) sits at the end of a grouped settings row; 'field' is a
	 * bordered control under its own label, like the form's other inputs.
	 */
	variant?: 'row' | 'field';
}

// The grey canvas behind the white cells, like iOS Settings
const CANVAS_CLASS = 'bg-zinc-100 dark:bg-zinc-950';

const WhenRow = ({
	title,
	checked,
	onSelect,
}: {
	title: string;
	checked: boolean;
	onSelect: () => void;
}) => (
	<CheckRow
		title={title}
		onSelect={onSelect}
		// A checklist: the role says it's on, the check only shows it
		buttonProps={{ role: 'checkbox', 'aria-checked': checked }}
		trailing={checked ? <Check aria-hidden className="h-4 w-4 text-primary" strokeWidth={3} /> : undefined}
	/>
);

const NotificationPanel = ({
	kind,
	value,
	onChange,
	allDay,
	people,
}: {
	kind: NotificationKind;
	value: Notifications;
	onChange: (value: Notifications) => void;
	allDay: boolean;
	people: ReturnType<typeof notificationPeople>;
}) => {
	const isNone = value.offsets.length === 0;
	return (
		<div className="space-y-5">
			<Group title="When">
				<CheckList label="When">
					<WhenRow
						title="None"
						checked={isNone}
						onSelect={() => onChange({ ...value, offsets: [] })}
					/>
					{whenOptions(kind).map(({ offset, label }) => (
						<WhenRow
							key={offset}
							title={label}
							checked={value.offsets.includes(offset)}
							onSelect={() => onChange(toggleOffset(value, offset))}
						/>
					))}
				</CheckList>
			</Group>
			{/* None keeps these values, so picking a time brings them back as they were */}
			{!isNone && (
				<>
					<Group title="How">
						<SwitchRow
							title="App notification"
							checked={value.push}
							onCheckedChange={(push) => onChange({ ...value, push })}
						/>
						<SwitchRow
							title="Email"
							checked={value.email}
							onCheckedChange={(email) => onChange({ ...value, email })}
						/>
					</Group>
					<Group title="Who">
						<SwitchRow
							title="Assigned"
							caption={assignedCaption(people)}
							checked={value.toAssignees}
							onCheckedChange={(toAssignees) => onChange({ ...value, toAssignees })}
						/>
						<SwitchRow
							title="Creator"
							caption={creatorCaption(people)}
							checked={value.toCreator}
							onCheckedChange={(toCreator) => onChange({ ...value, toCreator })}
						/>
					</Group>
				</>
			)}
			<p aria-live="polite" className={cn('px-4 text-[13px] leading-snug', CAPTION_TONE_CLASS)}>
				{notificationSentence({ notifications: value, kind, people, allDay })}
			</p>
		</div>
	);
};

/**
 * The Notify row's button and its panel: a popover from 768px, a bottom sheet
 * on phones. Put it in a form's `Row`, whose label opens it too.
 */
export const NotificationSettingsField = ({
	id,
	kind,
	value,
	onChange,
	allDay = false,
	assigneeIds,
	departmentId,
	creatorId,
	isNew = false,
	disabled,
	reopen = false,
	onOpenChange,
	variant = 'row',
}: NotificationSettingsFieldProps) => {
	const viewerId = useSelector((state: RootState) => state.auth?.user?._id as string | undefined);
	const displayNames = useUserDisplayNames();
	const departments = useLocationDepartments();

	const people = notificationPeople({
		names: displayNames,
		viewerId,
		_assignees: assigneeIds,
		department: departments.find((department) => department._id === departmentId)?.name,
		// A saved item without one (older jobs) has nobody to fall back on
		creator: isNew ? undefined : creatorId ?? null,
	});
	const summary = notificationSummary(value, kind, people);

	// Open state stays up here so rotating across 768px keeps the panel open
	const isMobile = useIsMobile();
	const isWide = useMediaQuery(SCREEN_SIZES.xl);
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const changeOpen = (next: boolean) => {
		setOpen(next);
		onOpenChange?.(next);
	};

	// Once the form has re-laid out, bring the row back into view for the
	// popover to hang from.
	const showRowLater = () =>
		window.setTimeout(() => {
			const trigger = triggerRef.current;
			const rect = trigger?.getBoundingClientRect();
			if (rect && (rect.top < 0 || rect.bottom > window.innerHeight))
				trigger?.scrollIntoView({ block: 'center' });
		}, 300);

	// Rotated from the sheet to the popover
	const wasMobile = useRef(isMobile);
	useEffect(() => {
		const rotatedOpen = wasMobile.current && !isMobile && open;
		wasMobile.current = isMobile;
		if (!rotatedOpen) return;
		const timer = showRowLater();
		return () => window.clearTimeout(timer);
	}, [isMobile, open]);

	// Focus the row first: the panel hands focus back to what had it when it
	// opened. Opening after this commit also puts it above a parent dialog.
	useLayoutEffect(() => {
		if (!reopen) return;
		triggerRef.current?.focus({ preventScroll: true });
		setOpen(true);
		const timer = showRowLater();
		return () => window.clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const panel = (
		<NotificationPanel kind={kind} value={value} onChange={onChange} allDay={allDay} people={people} />
	);

	return (
		<>
			<Popover open={open && !isMobile} onOpenChange={changeOpen} modal>
				<PopoverTrigger asChild>
					<button
						ref={triggerRef}
						id={id}
						type="button"
						disabled={disabled}
						aria-label={`Notify: ${summary}`}
						aria-haspopup="dialog"
						aria-expanded={open}
						// The sheet isn't this popover's content
						{...(isMobile ? { 'aria-controls': undefined } : {})}
						className={
							variant === 'field'
								? // Looks like the form's other inputs
									'flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-sm outline-none transition-colors hover:bg-accent/40 focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50 max-md:h-11 max-md:text-base'
								: // Fills the row's height, so all of it is the target
									'-my-1.5 flex min-h-11 w-full min-w-0 items-center justify-end gap-1.5 rounded-sm py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'
						}
					>
						<span className={cn('truncate', variant === 'field' ? 'text-foreground' : 'text-muted-foreground')}>
							{summary}
						</span>
						<ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground/80" />
					</button>
				</PopoverTrigger>
				{!isMobile && (
					<PopoverContent
						aria-label="Notify"
						// Wide screens have room beside the form for the whole panel
						side={isWide ? 'right' : 'bottom'}
						align={isWide ? 'center' : 'end'}
						collisionPadding={8}
						className={cn(
							'w-[22rem] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overscroll-contain p-3',
							CANVAS_CLASS,
						)}
					>
						{panel}
					</PopoverContent>
				)}
			</Popover>
			{isMobile && (
				<BottomSheet
					open={open}
					onOpenChange={changeOpen}
					// The sheet may have opened from the popover after a rotation, with
					// nothing left to hand focus back to: use the row.
					onCloseAutoFocus={(event) => {
						const active = document.activeElement;
						if (active && active !== document.body) return;
						event.preventDefault();
						triggerRef.current?.focus({ preventScroll: true });
					}}
					title="Notify"
					className={CANVAS_CLASS}
					headerRight={
						<BottomSheetTextButton strong onClick={() => changeOpen(false)}>
							Done
						</BottomSheetTextButton>
					}
				>
					{panel}
				</BottomSheet>
			)}
		</>
	);
};
